<template>
  <div class="tw-m-2">
    <div>
      <router-link class="nav-link mx-2 has-text-weight-semibold" :to="{ name: 'new-building' }">
        Gebouw aanmaken
      </router-link>
    </div>
    <div>
      <div v-if="isLoading" class="tw-flex tw-gap-5 tw-items-center">
        <div
          class="tw-border-gray-300 tw-m-5 tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-8 tw-border-t-orange-600"
        />
        Laden...
      </div>
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>Naam</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="building in buildings" :key="building.id">
            <td class="tw-p-1">{{ building.id }}</td>
            <td class="tw-p-1">
              <router-link :to="{ name: 'building-details', params: { id: building.id } }">
                {{ building.name }}
              </router-link>
            </td>
            <td class="tw-p-1">
              <button
                @click="openDeleteModal(building)"
                class="tw-text-sm tw-bg-red-200 hover:tw-bg-red-300 tw-rounded tw-py-1 tw-px-2"
              >
                <div class="tw-flex tw-gap-2 tw-items-center">
                  <TrashIcon class="tw-h-5 tw-w-5" />
                  <span class="tw-hidden sm:tw-inline">Verwijderen</span>
                </div>
              </button>
            </td>
          </tr>
        </tbody>

        <generic-modal
          v-if="showModal"
          @close="showModal = false"
          @submit="confirmDelete"
          :title="'Gebouw verwijderen?'"
        >
          <div>
            Weet je zeker dat je het gebouw <strong>{{ buildingToDelete?.name }}</strong> wilt
            verwijderen?
          </div>
        </generic-modal>
      </table>

      <button @click="fetchNextPage" v-if="hasNextPage">Meer...</button>
    </div>
  </div>
</template>

<script setup>
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { TrashIcon } from '@vue-hero-icons/outline'
import notifications from '@/util/Notifications'
import { ref, computed } from 'vue'
import GenericModal from '@/components/shared/GenericModal.vue'

const perPage = 30

const getBuildings = async ({ pageParam = 1 }) => {
  const urlParams = new URLSearchParams({
    offset: (pageParam - 1) * perPage,
    limit: perPage,
  })

  try {
    const result = await fetch(`/tickets/api/v1/buildings?${urlParams}`)
    if (!result.ok) {
      notifications.addNotification({
        message: `Failed to get list of buildings: ${result.statusText}`,
        type: 'danger',
      })
      return
    }

    return await result.json()
  } catch (error) {
    notifications.addNotification({
      message: `Failed to get list of buildings, ${e.message || e.detail}`,
      type: 'danger',
    })
    throw new Error('Failed to get list of buildings with error: ' + error.message)
  }
}

const { data, isLoading, fetchNextPage, hasNextPage } = useInfiniteQuery({
  queryKey: computed(() => ['buildings']),
  keepPreviousData: true,
  queryFn: getBuildings,
  getNextPageParam: (_, allPages) =>
    allPages[allPages.length - 1].data?.length === perPage ? allPages.length + 1 : undefined,
})

const buildings = computed(() => data.value?.pages.flatMap((page) => page.data) || [])

const queryClient = useQueryClient()

const deleteBuilding = async (id) => {
  const result = await fetch(`/tickets/api/v1/buildings/${id}`, {
    method: 'DELETE',
  })

  if (!result.ok) {
    const errorMsg = await result.text()
    throw new Error(`Fout bij verwijderen: ${errorMsg || result.statusText}`)
  }
}

const mutation = useMutation({
  mutationFn: deleteBuilding,
  onSuccess: () => {
    queryClient.invalidateQueries({ queryKey: ['buildings'] })
    notifications.addNotification({
      message: 'Gebouw succesvol verwijderd.',
      type: 'success',
    })
  },
  onError: (error) => {
    notifications.addNotification({
      message: `Verwijderen mislukt: ${error.message}`,
      type: 'danger',
    })
  },
})

const showModal = ref(false)
const buildingToDelete = ref(null)

const openDeleteModal = (building) => {
  buildingToDelete.value = building
  showModal.value = true
}

const confirmDelete = () => {
  if (!buildingToDelete.value) return

  mutation.mutate(buildingToDelete.value.id)

  showModal.value = false
  buildingToDelete.value = null
}
</script>
